import React, { useEffect, useRef, useState } from 'react';

import { errorMessage, isUnauthorized, updateScoreboard } from '../../api';
import { MAX_TIMER_MINUTES, POINTS, TEAMS, clock } from '../../scoreboard';
import useCountdown from '../../useCountdown';

const CONFIRM_MS = 6000; // resetting the score and ending the timer are live: they need a second tap
const DEFAULT_MINUTES = 5;

/**
 * The team games, run from the panel: points for each team (10 to 30 a tap), the score back
 * to zero, and the timer of the game being played, which starts with the minutes typed here
 * and can be ended before it runs out, for the games nobody knows how long will take.
 */
const ScoreboardControls = ({ adminKey, scoreboard, onChange, onUnauthorized }) => {
  const [subtracting, setSubtracting] = useState(false); // the next tap takes points away, to fix a mistake
  const [minutes, setMinutes] = useState(DEFAULT_MINUTES);
  const [confirming, setConfirming] = useState(null); // 'reset' | 'stop' while the second tap is awaited
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null); // { block: 'score' | 'timer', text }, shown where it happened
  const confirmTimer = useRef(null);
  const { seconds, running } = useCountdown(scoreboard.timer);

  useEffect(() => () => clearTimeout(confirmTimer.current), []);

  const askConfirm = (action) => {
    setConfirming(action);
    clearTimeout(confirmTimer.current);
    confirmTimer.current = setTimeout(() => setConfirming(null), CONFIRM_MS);
  };

  // Every change goes live on every phone: the panel says so if the room missed it
  const send = async (block, change) => {
    clearTimeout(confirmTimer.current);
    setConfirming(null);
    setSending(true);
    setError(null);
    try {
      const { scoreboard: next, announced } = await updateScoreboard(adminKey, change);
      onChange({ ...next, live: true });
      if (!announced) setError({ block, text: 'Quedó guardado, pero no se pudo anunciar en la sala' });
    } catch (err) {
      if (isUnauthorized(err)) onUnauthorized();
      else setError({ block, text: errorMessage(err) });
    } finally {
      setSending(false);
    }
  };

  // Taking points away is for one mistake: the tap after it adds again
  const give = (team, points) => {
    setSubtracting(false);
    send('score', { action: 'add', team, points: subtracting ? -points : points });
  };

  const minutesOk = Number.isInteger(minutes) && minutes >= 1 && minutes <= MAX_TIMER_MINUTES;
  const anyPoints = TEAMS.some(({ id }) => scoreboard[id] > 0);

  return (
    <>
      <section className='admin__block'>
        <h2 className='admin__block-title'>Marcador</h2>
        {TEAMS.map(({ id, label }) => (
          <div key={id} className='score'>
            <div className='score__head'>
              <span className='score__team'>{label}</span>
              <span className='score__value'>{scoreboard[id]}</span>
            </div>
            <div className='score__points' role='group' aria-label={`Puntos para ${label}`}>
              {POINTS.map((points) => (
                <button
                  key={points}
                  type='button'
                  className={`score__btn${subtracting ? ' score__btn--subtract' : ''}`}
                  aria-label={`${subtracting ? 'Restar' : 'Sumar'} ${points} a ${label}`}
                  disabled={sending || (subtracting && scoreboard[id] < points)}
                  onClick={() => give(id, points)}
                >
                  {subtracting ? '−' : '+'}
                  {points}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div className='score__foot'>
          <button type='button' className='admin__signout' aria-pressed={subtracting} onClick={() => setSubtracting(!subtracting)}>
            {subtracting ? 'Volver a sumar' : 'Restar puntos'}
          </button>
          {confirming === 'reset' ? (
            <>
              <button type='button' className='admin__signout score__confirm' disabled={sending} onClick={() => send('score', { action: 'reset' })}>
                Confirmar: los dos a 0
              </button>
              <button type='button' className='panel__cancel' onClick={() => setConfirming(null)}>
                Cancelar
              </button>
            </>
          ) : (
            <button type='button' className='admin__signout' disabled={sending || !anyPoints} onClick={() => askConfirm('reset')}>
              Resetear el marcador
            </button>
          )}
        </div>
        <p className='panel__hint'>
          {subtracting
            ? 'El próximo toque resta esos puntos, para corregir un error. Después vuelve a sumar.'
            : 'Cada toque suma esos puntos al equipo, en todos los celulares y en la pantalla del evento.'}
        </p>
        {error?.block === 'score' && <p className='form-error score__error'>{error.text}</p>}
      </section>

      <section className='admin__block'>
        <h2 className='admin__block-title'>Tiempo</h2>
        {running ? (
          <>
            <p className='score__clock'>{clock(seconds)}</p>
            {confirming === 'stop' ? (
              <div className='panel__confirm'>
                <button type='button' className='btn-primary btn-big' disabled={sending} onClick={() => send('timer', { action: 'stop' })}>
                  Confirmar: finalizar
                </button>
                <button type='button' className='panel__cancel' onClick={() => setConfirming(null)}>
                  Cancelar
                </button>
              </div>
            ) : (
              <button type='button' className='btn-primary btn-big' disabled={sending} onClick={() => askConfirm('stop')}>
                Finalizar el tiempo
              </button>
            )}
            <p className='panel__hint'>Corre en todos los celulares. Si el juego termina antes, finalizarlo deja el reloj en 00:00.</p>
          </>
        ) : (
          <>
            <div className='range'>
              <label className='range__field'>
                <input
                  className='input range__input'
                  type='number'
                  inputMode='numeric'
                  min='1'
                  max={MAX_TIMER_MINUTES}
                  aria-label='Minutos'
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value === '' ? '' : Number(e.target.value))}
                />
                minutos
              </label>
            </div>
            <button
              type='button'
              className='btn-primary btn-big'
              disabled={sending || !minutesOk}
              onClick={() => send('timer', { action: 'start', minutes })}
            >
              Iniciar el tiempo
            </button>
            <p className='panel__hint'>
              {minutesOk
                ? `Cuenta ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'} hacia atrás en todos los celulares. Se puede finalizar antes.`
                : `El tiempo va de 1 a ${MAX_TIMER_MINUTES} minutos.`}
            </p>
          </>
        )}
        {error?.block === 'timer' && <p className='form-error score__error'>{error.text}</p>}
      </section>
    </>
  );
};

export default ScoreboardControls;
